import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logoYork from "../../assets/york.png";

/**
 * Termo de responsabilidade, guarda e uso de equipamentos de TI, gerado a partir do ativo:
 * responsável, cargo, unidade e equipamentos (o ativo e seus componentes vinculados) saem preenchidos.
 * Quando o ativo não tem responsável, os campos ficam em branco para preenchimento manual.
 *
 * Os textos do termo ficam concentrados abaixo para facilitar a revisão pelo jurídico/RH.
 */

const MARGEM = 15;
const LARGURA_PAGINA = 210;
const ALTURA_PAGINA = 297;
const LARGURA_UTIL = LARGURA_PAGINA - 2 * MARGEM;
const LIMITE_INFERIOR = ALTURA_PAGINA - 20;

const EMPRESA = "DISTRIBUIDORA YORK LTDA";
const RODAPE = [
    "Rua Waldemar Rocha, 2804. Parque Ideal. 64078-640.",
    "Teresina - Piauí - Brasil - WWW.distribuidorayork.com.br",
    "Tel: (086) 3236 - 4215",
    "CNPJ: 07.230.451/0001-64",
];

const TITULO = ["TERMO DE RESPONSABILIDADE", "GUARDA E USO DE EQUIPAMENTOS DE T.I"];

function textoIntroducao({ nome, cargo, cpf, unidade }) {
    return `EU, ${nome}, Cargo de ${cargo}, CPF: ${cpf}, declaro que recebi da ${EMPRESA} `
        + "os materiais/equipamentos listados abaixo, para uso exclusivo de trabalho, comprometendo-me a mantê-los "
        + "em perfeito estado de conservação, ficando ciente que:";
}

function textoDepositario({ plural, objeto }) {
    const sujeito = plural ? "Estes encontram-se" : "Este encontra-se";
    const ativo = plural ? "destes ativos" : "deste ativo";
    const posse = plural ? `Por estarem de posse dos ${objeto}s` : `Por estar de posse do ${objeto}`;
    return `${sujeito} em perfeitas condições de uso e conservação, pelo qual me torno responsável, zelando pelo `
        + `bom uso, guarda e proteção ${ativo} da companhia, assumindo os encargos do fiel depositário, nos termos `
        + `do Artigo 627 do Código Civil Brasileiro. Estou ciente que o empréstimo ${ativo} a outro funcionário ou `
        + "subordinado, não transfere a responsabilidade do mesmo perante a Companhia. "
        + `${posse} em forma de comodato, comprometo-me a:`;
}

function clausulas({ mobile, objeto }) {
    return [
        mobile
            ? "Em caso de desligamento da empresa, devolver o aparelho e chip para a empresa. No caso de não "
            + "devolução, autorizar o pagamento do valor do aparelho, levando em consideração o valor de mercado "
            + "da operadora;"
            : "Em caso de desligamento da empresa, devolver o equipamento para a empresa. No caso de não "
            + "devolução, autorizar o pagamento do valor do equipamento, levando em consideração o valor de mercado;",
        `Informar ao setor responsável sempre que ocorrer algum problema que necessite de manutenção do ${objeto}, `
        + "arcando com parte do custo caso seja constatado o mau uso;",
        "Sempre que necessário e/ou programado, levar o equipamento ao setor de controle e suporte para as "
        + "devidas atualizações e backups;",
        mobile
            ? "Em caso de roubo, extravio ou perda total do aparelho, solicitar a operadora o bloqueio da linha, "
            + "ficando sujeito a pagamento do aparelho, com base no valor de mercado, havendo reposição somente "
            + "após quitação do débito;"
            : "Em caso de roubo, extravio ou perda total do equipamento, comunicar imediatamente o setor de TI, "
            + "ficando sujeito a pagamento do equipamento, com base no valor de mercado, havendo reposição somente "
            + "após quitação do débito;",
        "Estando os equipamentos em minha posse, estarei sujeito a inspeções sem prévio aviso;",
    ];
}

function textoFinal({ objeto }) {
    return `Entendo ainda que, a posse de ${objeto}s fora do horário de expediente não configura um plantão de trabalho.`;
}

// ------------------------------------------------------------------ dados do ativo

const LINHA = "____________________________________";

function ou(valor, preenchimento) {
    const v = (valor ?? "").toString().trim();
    return v || preenchimento;
}

/** Linhas de descrição de um equipamento: tipo/marca/modelo, S/N, patrimônio e dados específicos. */
function descricaoDoAtivo(a) {
    const linhas = [];
    const principal = [a.tipoDispositivoNome, a.marcaNome, a.modeloNome].filter(Boolean).join(" ");
    if (principal) linhas.push(principal);
    if (a.numeroSerie) linhas.push(`S/N: ${a.numeroSerie}`);
    if (a.patrimonio) linhas.push(`Patrimônio: ${a.patrimonio}`);
    if (a.nomenclatura) linhas.push(`Nomenclatura: ${a.nomenclatura}`);
    if (a.categoria === "MOBILE") {
        if (a.imei) linhas.push(`IMEI: ${a.imei}`);
        if (a.numeroLinha) linhas.push(`Linha: ${a.numeroLinha}${a.operadoraNome ? ` (${a.operadoraNome})` : ""}`);
        if (a.chip) linhas.push(`Chip: ${a.chip}`);
    }
    return linhas.length ? linhas.join("\n") : `Ativo #${a.id}`;
}

function descricaoDoComponente(c) {
    const linhas = [[c.tipoDispositivoNome, c.descricao].filter(Boolean).join(" - ")];
    if (c.numeroSerie && c.numeroSerie !== c.descricao) linhas.push(`S/N: ${c.numeroSerie}`);
    return linhas.join("\n");
}

function carregarImagem(src) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
    });
}

function nomeArquivo(a) {
    const base = a.patrimonio || a.nomenclatura || a.numeroSerie || `ativo-${a.id}`;
    return `termo-responsabilidade-${String(base).replace(/[^\w.-]+/g, "_")}.pdf`;
}

// ------------------------------------------------------------------ geração

/**
 * Gera e baixa o termo em PDF.
 * @param ativo AtivoDTO completo (inclui `componentes`)
 */
export async function gerarTermoResponsabilidade(ativo) {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const mobile = ativo.categoria === "MOBILE";
    const objeto = mobile ? "aparelho" : "equipamento";
    const componentes = ativo.componentes || [];
    const plural = componentes.length > 0;

    let y = MARGEM;

    const alturaLinha = () => (doc.getFontSize() * doc.getLineHeightFactor()) / doc.internal.scaleFactor;
    const garantirEspaco = (altura) => {
        if (y + altura > LIMITE_INFERIOR) {
            doc.addPage();
            y = MARGEM;
        }
    };
    const paragrafo = (texto, { recuo = 0, apos = 4, negrito = false, tamanho = 10, alinhamento = "justify" } = {}) => {
        doc.setFont("helvetica", negrito ? "bold" : "normal");
        doc.setFontSize(tamanho);
        const largura = LARGURA_UTIL - recuo;
        const quantidade = doc.splitTextToSize(texto, largura).length;
        const altura = quantidade * alturaLinha();
        garantirEspaco(altura);
        const x = alinhamento === "right" ? MARGEM + LARGURA_UTIL : MARGEM + recuo;
        doc.text(texto, x, y, { maxWidth: largura, align: alinhamento, baseline: "top" });
        y += altura + apos;
    };

    // Cabeçalho: logo e título
    const logo = await carregarImagem(logoYork);
    if (logo) {
        const altura = 16;
        const largura = (altura * logo.width) / logo.height;
        doc.addImage(logo, "PNG", MARGEM, y, largura, altura);
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text(TITULO, MARGEM + LARGURA_UTIL, y + 4, { align: "right", baseline: "top" });
    y += 24;

    // Identificação do responsável
    paragrafo(textoIntroducao({
        nome: ou(ativo.responsavelNome, LINHA),
        cargo: ou(ativo.responsavelCargo, "___________________________"),
        cpf: "_______________________",
        unidade: ou(ativo.filialNome, "____________________________"),
    }), { apos: 5 });

    // Equipamentos: o ativo e seus componentes vinculados
    const itens = [[ "01", descricaoDoAtivo(ativo) ]];
    componentes.forEach((c, i) => itens.push([String(i + 2).padStart(2, "0"), descricaoDoComponente(c)]));
    autoTable(doc, {
        startY: y,
        head: [["Item", "DESCRIÇÃO (Marca/modelo/SN)"]],
        body: itens,
        theme: "grid",
        margin: { left: MARGEM, right: MARGEM },
        styles: { font: "helvetica", fontSize: 9, cellPadding: 2, lineColor: [150, 150, 150], lineWidth: 0.1, textColor: 20 },
        headStyles: { fillColor: [204, 204, 204], textColor: 20, fontStyle: "bold", halign: "center" },
        columnStyles: { 0: { cellWidth: 14, halign: "center", fontStyle: "bold" } },
    });
    y = doc.lastAutoTable.finalY + 6;

    // Compromissos
    paragrafo(textoDepositario({ plural, objeto }), { apos: 3 });
    clausulas({ mobile, objeto }).forEach((texto, i) => {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        const quantidade = doc.splitTextToSize(texto, LARGURA_UTIL - 14).length;
        garantirEspaco(quantidade * alturaLinha());
        doc.text(`${i + 1}.`, MARGEM + 6, y, { baseline: "top" });
        doc.text(texto, MARGEM + 14, y, { maxWidth: LARGURA_UTIL - 14, align: "justify", baseline: "top" });
        y += quantidade * alturaLinha() + 2.5;
    });
    y += 2;
    paragrafo(textoFinal({ objeto }), { apos: 8 });

    // Local, data e assinatura
    const data = new Date().toLocaleDateString("pt-BR", { day: "numeric", month: "long", year: "numeric" });
    paragrafo(`_____________________ (___), ${data}.`, { alinhamento: "right", apos: 10 });
    paragrafo("Ciente (Assinatura): ___________________________________________", { alinhamento: "left", apos: 8 });

    // Devolução
    garantirEspaco(44);
    autoTable(doc, {
        startY: y,
        head: [[{ content: "Devolução", colSpan: 2, styles: { halign: "center" } }]],
        body: [[
            "Atestamos que o(s) bem(ns) devolvidos estão:\n"
            + "(   ) Em perfeito estado\n"
            + "(   ) Apresentando defeito. Qual(is)? ________________________________\n"
            + "(   ) Faltando peças/acessórios. Qual(is)? ___________________________",
            "\nData: ___ / ___ / ______\n\nAssinatura/Carimbo do Responsável\npelo Recebimento",
        ]],
        theme: "grid",
        margin: { left: MARGEM, right: MARGEM },
        styles: { font: "helvetica", fontSize: 8.5, cellPadding: 3, lineColor: [150, 150, 150], lineWidth: 0.1, textColor: 20 },
        headStyles: { fillColor: [204, 204, 204], textColor: 20, fontStyle: "bold" },
        columnStyles: { 1: { halign: "center", cellWidth: 62 } },
    });
    y = doc.lastAutoTable.finalY + 6;

    // Rodapé institucional fixo na base da última página
    const alturaRodape = RODAPE.length * 3.6;
    const yRodape = ALTURA_PAGINA - 10 - alturaRodape;
    if (y > yRodape - 2) {
        doc.addPage();
    }
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    RODAPE.forEach((linha, i) => {
        doc.text(linha, LARGURA_PAGINA / 2, yRodape + i * 3.6, { align: "center", baseline: "top" });
    });

    doc.save(nomeArquivo(ativo));
}
